import { MouseEvent, useMemo } from 'react';

import { useTheme } from '@mui/material';

import { shapeSizeMap } from './constants';
import { ScatterShapeProps } from './types';
import { defaultLabelFormatter, getShapeColor } from './utils';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { tokens } from '../../../../theme/generated/tokens';
import { getThemeColor } from '../../../../utils/theme';

type Props<T, V extends string | number = string | number> = {
  shapeProps?: ScatterShapeProps<V> | ((payload?: T) => ScatterShapeProps<V>);
  cx?: number;
  cy?: number;
  payload?: T;
  dataKey?: keyof T;
  index?: number;
  isActive?: boolean;
  onClick?: (payload: T, dataIndex: number, event: MouseEvent) => void;
  testId?: string;
  highlightedDatapoint?: string;
};

const defaultShapeProps: ScatterShapeProps<any> = {
  labelFormatter: defaultLabelFormatter,
  shapeColor: 'indigo.400',
  shapeSize: 'medium',
};

export const ScatterShape = <T, V extends string | number = string | number>({
  cx = 0,
  cy = 0,
  payload,
  isActive = false,
  onClick,
  dataKey,
  index: dataIndex,
  shapeProps = defaultShapeProps,
  testId = 'scatter-shape',
  highlightedDatapoint,
}: Props<T, V>) => {
  const theme = useTheme();
  const {
    shapeSize = 'medium',
    labelFormatter = defaultLabelFormatter,
    shapeColor = 'indigo.400',
  } = useMemo(
    () => (typeof shapeProps === 'function' ? shapeProps(payload) : shapeProps),
    [payload, shapeProps]
  );

  const isHighlighted = highlightedDatapoint === String(dataIndex);
  const isDisabled = !!highlightedDatapoint && !isHighlighted;

  const resolvedFill = getThemeColor(
    theme,
    mapPaletteColorToDataColorToken(
      getShapeColor({ isActive, isDisabled, isHighlighted, shapeColor }),
      shapeColor
    )
  );
  const resolvedRadius = shapeSizeMap[shapeSize];
  const coordinate = resolvedRadius + 2;
  const canHaveLabel = ['xlarge', 'xxlarge'].includes(shapeSize);

  if (
    !dataKey ||
    !payload ||
    payload[dataKey] === null ||
    payload[dataKey] === undefined
  ) {
    return null;
  }

  const pointValue = payload[dataKey] as V;
  const label = canHaveLabel ? labelFormatter(pointValue) : pointValue;

  return (
    <svg
      x={cx - resolvedRadius - 2}
      y={cy - resolvedRadius - 2}
      data-testid={`${testId}-${dataIndex}`}
      cursor={onClick ? 'pointer' : undefined}
      focusable="false"
      tabIndex={-1}
      style={{ outline: 'none' }}
      onClick={onClick ? (e) => onClick(payload, dataIndex ?? 0, e) : undefined}
      opacity={isDisabled ? 0.15 : 1}
    >
      <circle
        fill={resolvedFill}
        cx={coordinate}
        cy={coordinate}
        r={resolvedRadius}
      />

      {isActive && (
        <circle
          cx={coordinate}
          cy={coordinate}
          r={resolvedRadius + 1}
          strokeWidth={1}
          stroke={resolvedFill}
          fill="transparent"
        />
      )}

      {canHaveLabel && label && (
        <text
          x={coordinate}
          y={coordinate + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={12}
          fontFamily="Bai Jamjuree, sans-serif"
          fontStyle="normal"
          fontWeight={700}
          fill={tokens.theme.fg.structural.primaryOnInverse}
        >
          {label}
        </text>
      )}
    </svg>
  );
};
